import { memo, Dispatch, SetStateAction, useMemo, useState } from "react";
import { Box } from "@mui/material";
import SourceView from "src/views/assistant/components/sources/SourceView";
import ProvenancePanel from "src/views/assistant/components/sources/ProvenancePanel";
import {
  useDialogueProvenance,
  useProvenanceCapabilities,
} from "src/views/assistant/services/ProvenanceService";
import {
  overlayLiveProvenance,
  useLiveWebExternals,
} from "src/views/assistant/components/sources/useLiveWebExternals";

interface SourcesTabProps {
  sources: any[];
  documentHeight?: number;
  viewMode?: "dataChunk" | "cardList";
  setViewMode?: Dispatch<SetStateAction<"dataChunk" | "cardList">>;
  detailSourceViewSdrId?: number | null;
  detailSourceViewSourceId?: number | null;
  setDetailSourceViewSdrId?: Dispatch<SetStateAction<number | null>>;
  setDetailSourceViewSourceId?: Dispatch<SetStateAction<number | null>>;
  /**
   * Dialogue + message ids enable the three-tier provenance funnel
   * (Searched → Read → Cited). When absent (e.g. embedded chat surfaces that
   * don't pass them), the tab falls back to the legacy flat source list.
   */
  dialogueId?: number | null;
  messageId?: string | null;
}

/**
 * SourcesTab - Reusable wrapper for sources display
 *
 * When a dialogue id is available AND the backend reports the v2 capability
 * (``GET /datasets/provenance/capabilities`` → ``features.retrieval_events``)
 * it renders the three-tier provenance funnel (Searched → Read → Cited) backed
 * by ``POST /datasets/messages/provenance``. Otherwise — no dialogue, a legacy backend
 * (capabilities 404/error), or the feature flag off — it falls back to the
 * legacy flat {@link SourceView} (contract: feature absence never breaks).
 *
 * Handles missing detail-view state internally so clicking sources always works.
 */
const SourcesTab = memo(
  ({
    sources,
    documentHeight = 500,
    viewMode,
    setViewMode,
    detailSourceViewSdrId: externalDetailSdrId,
    detailSourceViewSourceId: externalDetailSourceId,
    setDetailSourceViewSdrId: externalSetDetailSdrId,
    setDetailSourceViewSourceId: externalSetDetailSourceId,
    dialogueId,
    messageId,
  }: SourcesTabProps) => {
    // Internal state for detail view IDs if not provided externally
    const [internalDetailSdrId, setInternalDetailSdrId] = useState<
      number | null
    >(null);
    const [internalDetailSourceId, setInternalDetailSourceId] = useState<
      number | null
    >(null);

    const detailSourceViewSdrId =
      externalDetailSdrId !== undefined
        ? externalDetailSdrId
        : internalDetailSdrId;
    const detailSourceViewSourceId =
      externalDetailSourceId !== undefined
        ? externalDetailSourceId
        : internalDetailSourceId;
    const setDetailSourceViewSdrId =
      externalSetDetailSdrId || setInternalDetailSdrId;
    const setDetailSourceViewSourceId =
      externalSetDetailSourceId || setInternalDetailSourceId;

    // Capability negotiation (#20423 close-gaps): GET /provenance/capabilities
    // decides legacy-vs-v2. Gating precedence:
    //   1. capabilities fetch error (404 on a legacy backend is the normal
    //      case) → legacy SourceView;
    //   2. capabilities ok but features.retrieval_events off → legacy;
    //   3. features on → the v2 ProvenancePanel — an EMPTY payload renders the
    //      panel's own empty/degradation states, it never bounces to legacy.
    const capabilities = useProvenanceCapabilities();
    const v2Enabled =
      capabilities.isSuccess &&
      capabilities.data?.features?.retrieval_events === true;

    // The Sources panel shows the SESSION-accumulated funnel (every assistant
    // turn merged with dialogue-global citation numbers), not just the latest
    // response (#20423 Story 3). messageId is still threaded through for the
    // per-response export action. Only fetched once the v2 capability is
    // confirmed, so legacy backends aren't polled for an endpoint they lack.
    const { data: provenance, isLoading: provenanceLoading } =
      useDialogueProvenance(dialogueId, { enabled: v2Enabled });

    // Live sources come straight from the chat's streaming store, before (or
    // without) any provenance fetch: web externals the instant the user
    // approves the search (#20423 A2), and chunk sources the moment the answer
    // cites them — the `sources` prop already holds the chunk cards from the
    // sources metadata events. Both overlays keep the backend payload
    // authoritative and only append what it doesn't own yet, so a still-
    // streaming (or stopped-mid-generation) turn's sources show live instead
    // of "no documents" until the post-turn fetch lands.
    const live = useLiveWebExternals(dialogueId);
    const resolved = useMemo(
      () => overlayLiveProvenance(provenance, live, sources),
      [provenance, live, sources],
    );

    // Loading guard: while either the capability handshake or the provenance
    // fetch is in flight (and we can address a dialogue), hold on the panel
    // spinner rather than flashing legacy-then-v2 (or vice versa).
    const gateLoading =
      dialogueId != null &&
      (capabilities.isLoading || (v2Enabled && provenanceLoading));

    if (dialogueId != null && (v2Enabled || gateLoading)) {
      return (
        // Claim the sidebar's bounded height (flex:1 + minHeight:0) and let
        // the panel scroll internally, so expanding a tier scrolls inside the
        // Sources sidebar instead of growing the whole page.
        <Box
          data-testid="aa-src-panel"
          sx={{
            flex: 1,
            minHeight: 0,
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ProvenancePanel
            provenance={resolved}
            isLoading={gateLoading}
            dialogueId={dialogueId}
            messageId={messageId}
          />
        </Box>
      );
    }

    return (
      <Box
        data-testid="aa-src-panel"
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
        }}
      >
        <SourceView
          responseSources={sources}
          documentHeight={documentHeight}
          viewMode={viewMode}
          setViewMode={setViewMode}
          detailSourceViewSdrId={detailSourceViewSdrId}
          detailSourceViewSourceId={detailSourceViewSourceId}
          setDetailSourceViewSdrId={setDetailSourceViewSdrId}
          setDetailSourceViewSourceId={setDetailSourceViewSourceId}
        />
      </Box>
    );
  },
);

SourcesTab.displayName = "SourcesTab";

export default SourcesTab;
